import React, { useEffect, useState, useRef } from 'react';
import { range } from 'd3-array'
import ReactDOM from 'react-dom';
import useWindowSize from './useWindowResize'
import { useInView } from 'react-intersection-observer'
import { simulator, initializeSimulationState, linkRenderer, nodesRenderer } from './withDeathSimulator'

const framesPerDay = 4
const percentOfHeight = 0.53
const zoom = 1.2

const ageGenerator = () => {
  return (Math.random() > 0.3) ? "adult" : ((Math.random() > 0.2) ? "elder" : "child")
}

const deathRates = {
  immunoNoBeds: 0.1,
  immunoWithBed: 0.8,
  severeNoBeds: 0.7,
  severeWithBeds: 0.95
};

const graphGenerator = (w, h, scaleFactor) => {
  const width = w * scaleFactor
  const height = h * scaleFactor

  const rowHeight = 130
  const columnWidth = 470

  const cores = []
  
  const numOfRows = Math.ceil(height / rowHeight)
  const numOfColumns = Math.ceil(width / columnWidth)

  range(numOfRows).forEach((row) => {
    range(numOfColumns).forEach((col) => {
      cores.push({
        y: rowHeight * (0.2 + row),
        x: columnWidth * (col + ((row % 2 === 0) ? 0.5 : 0) + 0.1)
      })
    })
  })

  const nodes = []

  const links = []

  const neighboursHash = {}

  const outerRingMembers = []

  const clusters = cores.map((c, i) => {
    const centroid = {
      id: (i === 0) ? "a" : `cluster-${i}-centroid`,
      age: ageGenerator(),
      immuno: false,
      x: c.x,
      y: c.y
    }

    const cluster = [centroid]

    nodes.push(centroid)
    neighboursHash[centroid.id] = {
      node: centroid,
      neighbours: []
    }

    const rings = (i === 0) ? 5 : 2 + Math.round(Math.random() * 2.3)

    range(rings).forEach((r) => {
      const nodesInRing = 5 + r * 3
      const radius = (r + 1) * (25 - r * 1.5)

      range(nodesInRing).forEach((n) => {
        const offset = r * (1 / nodesInRing) * 2 * Math.PI 
        const theta = (n / nodesInRing) * 2 * Math.PI + offset
        const cx = centroid.x + Math.cos(theta) * radius
        const cy = centroid.y + Math.sin(theta) * radius

        const age = ageGenerator()
        const immunocompromisedChance = (age === "elder") ? 0.2 : 0.015

        const node = {
          clusterId: i,
          id: `cluster-${i}-${r}-${n}`,
          age,
          immuno: (Math.random() < immunocompromisedChance) ? true : false,
          x: cx,
          y: cy
        }

        neighboursHash[node.id] = {
          node,
          neighbours: []
        }

        nodes.push(node)
        cluster.push(node)

        if (r === rings - 1) {
          outerRingMembers.push(node)
        }
      })
    })

    return cluster
  })

  const clusterConnectionHash = {}
  const shouldRandomlyConnect = (nid, mid) => {
    const pairId = (nid >= mid) ? `${mid}-${nid}` : `${nid}-${mid}`

    if (clusterConnectionHash[pairId]) {
      const c = clusterConnectionHash[pairId]
      const chance = Math.random() < (1 / (c + 1))

      if (chance) {
        clusterConnectionHash[pairId]++;
      }

      return chance
    } else {
      clusterConnectionHash[pairId] = 1
      return true
    }
  }

  outerRingMembers.forEach((n, i, array) => {
    const subset = array.slice(i + 1, array.length)

    subset.forEach((m) => {
      const dx = n.x - m.x
      const dy = n.y - m.y
      const dist = Math.hypot(dx, dy)

      if (dist < 210 && n.clusterId !== m.clusterId && shouldRandomlyConnect(n.clusterId, m.clusterId)) {
        links.push({ 
          source: n, 
          target: m, 
          priority: "IMMEDIATE-FAMILY" 
        })

        neighboursHash[n.id].neighbours.push({
          id: m.id,
          priority: "IMMEDIATE-FAMILY"
        })

        neighboursHash[m.id].neighbours.push({
          id: n.id,
          priority: "IMMEDIATE-FAMILY"
        })
      }
    })
  })

  clusters.forEach((cluster) => {
    cluster.forEach((n, i, array) => {
      const subset = array.slice(i + 1, array.length)

      subset.forEach((m) => {
        const dx = n.x - m.x
        const dy = n.y - m.y
        const dist = Math.hypot(dx, dy)

        if (dist < 65) {
          links.push({ 
            source: n, 
            target: m, 
            priority: "IMMEDIATE-FAMILY" 
          })

          neighboursHash[n.id].neighbours.push({
            id: m.id,
            priority: "IMMEDIATE-FAMILY"
          })
  
          neighboursHash[m.id].neighbours.push({
            id: n.id,
            priority: "IMMEDIATE-FAMILY"
          })
        }
      })
    })
  })
  
  return {
    nodes,
    links,
    hash: neighboursHash
  }
}

const HeaderGraphic = () => {
  const windowSize = useWindowSize()
  // console.log({ ...windowSize, iw: window.innerWidth, ih: window.innerHeight })

  const scaleFactor = window.devicePixelRatio;
  const [width, setWidth] = useState(windowSize.width)
  const [height, setHeight] = useState(windowSize.height * percentOfHeight)
  //const [graph, setGraph] = useState(graphGenerator(windowSize.width, height, scaleFactor))
  const initialGraph = graphGenerator(windowSize.width, height, scaleFactor)

  const [simulationState, setSimulationState] = React.useState({
    frame: 0,
    graph: initialGraph,
    state: initializeSimulationState(initialGraph)
  })

  const canvasRef = useRef()
  const linksCanvasRef = useRef()
  const rectRef = useRef()

  const [wrapperRef, inView, entry] = useInView({
    /* Optional options */
    threshold: 0,
  })

  const animate = useRef()
  const requestRef = useRef()

  React.useEffect(() => {
    animate.current = () => {
      const canvas = canvasRef.current
      const context = canvas.getContext('2d')

      const linksCanvas = linksCanvasRef.current
      const linksContext = linksCanvas.getContext('2d')

      let shouldStop = false

      setSimulationState((prevSimState) => {
        let graph = prevSimState.graph
        let nextSimulationState = prevSimState.state;
        const frame = prevSimState.frame

        if (inView || frame === 0) {
          // frameCount.current = (frameCount.current) ? frameCount.current + 1 : 1;
          const day = Math.floor(frame / framesPerDay)

          if (linkRenderer && frame < 10) {
            linksContext.clearRect(0, 0, width * scaleFactor, height * scaleFactor);
            linksContext.scale(zoom, zoom)
            linkRenderer(linksContext, graph.links, {})
            linksContext.resetTransform()
          }

          if (nodesRenderer) {
            context.clearRect(0, 0, width * scaleFactor, height * scaleFactor);
            context.scale(zoom, zoom)
            nodesRenderer(context, graph.nodes, nextSimulationState.population, day, frame, framesPerDay, {})
            context.resetTransform()
          }

          if (inView && frame % framesPerDay === 0 && prevSimState) {
            nextSimulationState = simulator(graph.nodes, graph.hash, nextSimulationState, { ...deathRates, numberOfBeds: 2 })
          }
        }

        shouldStop = (
          nextSimulationState.history[nextSimulationState.history.length - 1].SUSCEPTIBLE === 0 &&
          nextSimulationState.history[nextSimulationState.history.length - 1].INFECTED === 0
        )

        return {
          frame: (inView) ? frame + 1 : frame,
          graph,
          state: nextSimulationState
        }
      })

      if (!shouldStop) {
        requestRef.current = requestAnimationFrame(animate.current);
      }
    };
  });

  useEffect(() => {
    const rect = rectRef.current.getBoundingClientRect()

    window.cancelAnimationFrame(requestRef.current)

    if (width !== rect.width) {
      const height = window.innerHeight * percentOfHeight
      const width = rect.width

      const newGraph = graphGenerator(width, height, scaleFactor / zoom)

      setWidth(width)
      setHeight(height)
      
      setSimulationState({
        frame: 0,
        graph: newGraph,
        state: initializeSimulationState(newGraph)
      })

      animate.current()
    }
  }, [windowSize])

  React.useEffect(() => {
    animate.current()
  }, [])

  return ReactDOM.createPortal(
    <div ref={wrapperRef}>
      <div ref={rectRef} className="canvas-stack">
        <canvas className="links-canvas" ref={linksCanvasRef} width={width * scaleFactor} height={height * percentOfHeight * scaleFactor}
          style={{
            width: `${width}px`,
            height: `${height * percentOfHeight}px`,
          }}
        />
        <canvas className="nodes-canvas" ref={canvasRef} width={width * scaleFactor} height={height * percentOfHeight * scaleFactor}
          style={{
            width: `${width}px`,
            height: `${height * percentOfHeight}px`,
            borderBottom: "1px solid #aaa"
          }}
        />
      </div>
    </div>
  , document.getElementById("header-graphic"));
};

export default HeaderGraphic;